import { Injectable } from '@angular/core';

import { ChartData } from '../business/chart/common/chart-data';
import { DonutChartSegment } from '../business/chart/donut/donut-chart-segment';

/**
 * This Service is used for create the donut
 */
@Injectable({
    providedIn: 'root',
})
export class DonutChartFactoryService {
    /**
     * @type {number}
     * An offset of 25 means the top center of the chart
     */
    private readonly START_OFFSET_DEFAULT: number = 25;

    /**
     * Ranks chart segments from the smallest to the largest percentage
     * @param {DonutChartSegment[]} segments Segments to sort
     * @returns {DonutChartSegment[]} Array of sorted segments
     */
    private static sortSegments(segments: DonutChartSegment[]): DonutChartSegment[] {
        return segments.sort((a: DonutChartSegment, b: DonutChartSegment) => {
            return a.option.percent > b.option.percent ? -1 : 1;
        });
    }

    /**
     * Computes offset for each segments
     * @param {DonutChartSegment[]} segments Segments without offset
     * @param {number} startOffset
     */
    private static computeSegmentsOffset(segments: DonutChartSegment[], startOffset: number): void {
        let offset: number = startOffset;
        for (const segment of segments) {
            segment.option.initialOffset = offset;
            offset = segment.option.initialOffset - segment.option.percent;
        }
    }

    /**
     * Create a segment with simple parameters
     * @param {string} color
     * @param {number} percent
     * @param {number} radius
     * @param {number} borderWidth
     * @param {number?} startOffset
     * @returns {DonutChartSegment}
     */
    public createSegment(
        color: string,
        percent: number,
        radius: number,
        borderWidth: number,
        startOffset: number = this.START_OFFSET_DEFAULT,
    ): DonutChartSegment {
        return new DonutChartSegment({
            radius: radius,
            initialOffset: startOffset,
            color: color,
            percent: percent,
            borderWidth: borderWidth,
        });
    }

    /**
     * Create a segment with data
     * @param {ChartData} data
     * @param {number} radius
     * @param {number} borderWidth
     * @param {number = 25} startOffset
     * @returns {DonutChartSegment}
     */
    public createSegmentWithDonutChartData(
        data: ChartData,
        radius: number,
        borderWidth: number,
        startOffset: number = this.START_OFFSET_DEFAULT,
    ): DonutChartSegment {
        return this.createSegment(data.color, data.percentage, radius, borderWidth, startOffset);
    }

    /**
     * Creates and sorts segments to display in the chart the greather to the smaller
     * @param {ChartData[]} inputDataList List of input chart data
     * @param {number} radius
     * @param {number} borderWidth
     * @param {number = 25} startOffset
     * @returns {DonutChartSegment[]}
     */
    public createSortedSegments(
        inputDataList: ChartData[],
        radius: number,
        borderWidth: number,
        startOffset: number = this.START_OFFSET_DEFAULT,
    ): DonutChartSegment[] {
        const unsortedSegments: DonutChartSegment[] = [];
        for (const data of inputDataList) {
            unsortedSegments.push(this.createSegmentWithDonutChartData(data, radius, borderWidth));
        }

        const sortedSegments: DonutChartSegment[] = DonutChartFactoryService.sortSegments(unsortedSegments);
        DonutChartFactoryService.computeSegmentsOffset(sortedSegments, startOffset);

        return sortedSegments;
    }
}
